type LookupData = Record<string, string | null>;
type KvkSearchResult = { name: string; kvkNumber: string; city: string | null; street: string | null };

async function getJson(url: string): Promise<LookupData | null> {
    try {
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!res.ok) return null;
        const data = (await res.json()) as LookupData;
        return data && Object.keys(data).length > 0 ? data : null;
    } catch {
        return null;
    }
}

async function searchKvk(q: string): Promise<KvkSearchResult[]> {
    try {
        const res = await fetch(`/gratis-startmaand-aanvragen/kvk-search?q=${encodeURIComponent(q)}`, {
            headers: { Accept: 'application/json' },
        });
        if (!res.ok) return [];
        const data = await res.json();
        return Array.isArray(data) ? (data as KvkSearchResult[]) : [];
    } catch {
        return [];
    }
}

export function initLookups(form: HTMLFormElement): void {
    const field = (name: string) => form.querySelector<HTMLInputElement>(`[name="${name}"]`);
    const company = field('company_name');
    const kvkField = form.querySelector<HTMLInputElement>('[data-kvk-number]');
    const street = field('address_street');
    const houseNumber = field('address_house_number');
    const postalCode = field('address_postal_code');
    const city = field('address_city');
    const kvkUsed = field('kvk_lookup_used');
    const bagUsed = field('bag_lookup_used');
    const kvkStatus = form.querySelector<HTMLElement>('[data-kvk-status]');

    // KvK name autocomplete
    if (company && company.dataset.kvkSearch !== undefined) {
        initKvkSearch(company, kvkField, street, houseNumber, postalCode, city, kvkUsed, kvkStatus);
    }

    // BAG lookup on postal code + house number
    const setFromBag = async (): Promise<void> => {
        const pc = postalCode?.value.trim() ?? '';
        const nr = houseNumber?.value.trim() ?? '';
        if (pc === '' || nr === '') return;
        const data = await getJson(`/gratis-startmaand-aanvragen/bag?postcode=${encodeURIComponent(pc)}&nr=${encodeURIComponent(nr)}`);
        if (!data) return;
        if (street && data.street && street.value.trim() === '') street.value = data.street;
        if (city && data.city && city.value.trim() === '') city.value = data.city;
        if (bagUsed) bagUsed.value = '1';
    };

    postalCode?.addEventListener('blur', () => { void setFromBag(); });
    houseNumber?.addEventListener('blur', () => { void setFromBag(); });
}

function initKvkSearch(
    input: HTMLInputElement,
    kvkField: HTMLInputElement | null,
    street: HTMLInputElement | null,
    houseNumber: HTMLInputElement | null,
    postalCode: HTMLInputElement | null,
    city: HTMLInputElement | null,
    kvkUsed: HTMLInputElement | null,
    statusEl: HTMLElement | null,
): void {
    const list = document.createElement('ul');
    list.className = 'kvk-suggestions';
    list.setAttribute('role', 'listbox');
    list.hidden = true;
    input.closest('.kvk-search-wrap')?.appendChild(list);

    let debounceTimer = 0;
    let currentResults: KvkSearchResult[] = [];
    let activeIndex = -1;

    function clearSuggestions(): void {
        list.hidden = true;
        list.innerHTML = '';
        currentResults = [];
        activeIndex = -1;
    }

    function fillFromData(data: LookupData): void {
        if (street && data.street) street.value = data.street;
        if (houseNumber && data.houseNumber) houseNumber.value = data.houseNumber;
        if (postalCode && data.postalCode) postalCode.value = data.postalCode;
        if (city && data.city) city.value = data.city;
        if (kvkUsed) kvkUsed.value = '1';
        if (statusEl) statusEl.textContent = 'Gegevens opgehaald via KvK.';
    }

    function selectResult(result: KvkSearchResult): void {
        input.value = result.name;
        if (kvkField) kvkField.value = result.kvkNumber;
        clearSuggestions();
        if (statusEl) statusEl.textContent = 'Adresgegevens worden opgehaald…';
        void getJson(`/gratis-startmaand-aanvragen/kvk?nr=${encodeURIComponent(result.kvkNumber)}`).then(data => {
            if (data) fillFromData(data);
            else if (statusEl) statusEl.textContent = '';
        });
    }

    function updateActive(): void {
        Array.from(list.children).forEach((li, i) => {
            li.classList.toggle('is-active', i === activeIndex);
        });
    }

    function renderSuggestions(results: KvkSearchResult[]): void {
        list.innerHTML = '';
        activeIndex = -1;
        if (results.length === 0) { list.hidden = true; return; }
        results.forEach((r) => {
            const li = document.createElement('li');
            li.setAttribute('role', 'option');

            const line1 = document.createElement('span');
            line1.className = 'kvk-suggestion__name';
            line1.textContent = r.name;
            li.appendChild(line1);

            const addressParts = [r.street, r.city].filter(Boolean).join(', ');
            if (addressParts) {
                const line2 = document.createElement('span');
                line2.className = 'kvk-suggestion__address';
                line2.textContent = addressParts;
                li.appendChild(line2);
            }

            li.addEventListener('mousedown', (e) => { e.preventDefault(); selectResult(r); });
            list.appendChild(li);
        });
        list.hidden = false;
    }

    input.addEventListener('input', () => {
        clearTimeout(debounceTimer);
        const q = input.value.trim();
        if (q.length < 2) { clearSuggestions(); return; }
        debounceTimer = window.setTimeout(() => {
            void searchKvk(q).then(results => {
                currentResults = results;
                renderSuggestions(results);
            });
        }, 300);
    });

    input.addEventListener('keydown', (e) => {
        if (list.hidden) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            activeIndex = Math.min(activeIndex + 1, currentResults.length - 1);
            updateActive();
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            activeIndex = Math.max(activeIndex - 1, -1);
            updateActive();
        } else if (e.key === 'Enter' && activeIndex >= 0) {
            e.preventDefault();
            selectResult(currentResults[activeIndex]);
        } else if (e.key === 'Escape') {
            clearSuggestions();
        }
    });

    input.addEventListener('blur', () => { window.setTimeout(clearSuggestions, 150); });
}
